import { cfetch } from '@confluence/network';

import { cfetchWithAbort } from '../utils/cfetchWithAbort.ts';
import { IFS_API } from '../config/apiConstants.ts';

declare global {
	interface Window {
		__backendOriginHost: string | undefined;
	}
}

export const processJsonResponse = (response: any): any => {
	return response.json();
};

export const cfetchWithProcess = (url: any, options?: any): Promise<any> =>
	cfetchWithAbort(url, options).then(processJsonResponse);

export type UploadOptions = {
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
	document: Blob;
	pageTitle: string;
	space: string;
	importSource: string;
	parentPageId: string | null;
	originalDraftId?: string;
	isBulkUpload?: boolean;
	signal?: AbortSignal;
	subType?: string;
};

interface ResponseContentSpace {
	id: string | number;
	key: string;
}

interface ResponseContentLinks {
	context: string;
	editui: string;
}

export type ResponseContentMetadata = {
	id: string;
	space?: ResponseContentSpace;
	title: string;
	_links: ResponseContentLinks;
};

export type UploadResponse = {
	data: ResponseContentMetadata;
	errors?: [{ type: string }];
};

type CreateParentPage = {
	data: {
		id: string;
	};
	errors?: [{ type: string }];
};

export function getConfluenceApiBaseUrl(): string {
	return window.location.hostname === 'localhost'
		? getLocalhostBackendUrl()
		: window.location.origin;
}

function getLocalhostBackendUrl() {
	return window.__backendOriginHost
		? `https://${window.__backendOriginHost}`
		: 'https://pug.jira-dev.com';
}

async function getCSRFToken() {
	const result = await cfetchWithProcess(`${IFS_API}/csrf-token`, {
		credentials: 'include',
	});

	return result?.csrfToken;
}

export async function uploadFile(options: UploadOptions): Promise<UploadResponse> {
	const { signal } = options;
	delete options.signal; //remove not needed field in the body

	const formData = new FormData();
	Object.keys(options).forEach((key) => {
		if (options[key]) {
			// Update subType when uploading to new API
			if (key === 'subType') {
				options[key] = options[key].toUpperCase();
			}
			formData.append(key, options[key]);
		}
	});
	formData.append('apiUrl', getConfluenceApiBaseUrl());

	return cfetchWithProcess('/wiki/rest/internals/1.0/word-doc/upload', {
		method: 'POST',
		credentials: 'include',
		signal,
		body: formData,
	});
}

async function executeDelete(queryParams, endpoint) {
	const stringifiedQueryParams = new URLSearchParams({
		apiUrl: getConfluenceApiBaseUrl(),
		...queryParams,
	}).toString();
	const csrfToken = await getCSRFToken();
	const res = await cfetch(`${IFS_API}/${endpoint}?${stringifiedQueryParams}`, {
		method: 'delete',
		headers: {
			'csrf-token': csrfToken,
		},
	});
	return res.json();
}

export async function deletePage(contentId: any, params = {}): Promise<void> {
	await executeDelete({ contentId, ...params }, 'discard');
}

export async function deleteBulkPages(parentPageId: any, params = {}): Promise<void> {
	await executeDelete({ parentPageId, ...params }, 'discard-bulk-pages');
}

export async function createParentPage(
	space: string,
	parentPageId: string | null,
	restriction: string,
	subType?: string,
	pageTitle?: string,
	createAsFolder?: boolean,
): Promise<CreateParentPage> {
	const body: Record<string, unknown> = {
		space,
		parentPageId: parentPageId ? Number(parentPageId) : null,
		apiUrl: getConfluenceApiBaseUrl(),
		restriction,
		...(subType ? { subType } : {}),
		...(pageTitle ? { pageTitle } : {}),
		...(createAsFolder ? { createAsFolder: true } : {}),
	};
	// During migration, expected parameters for the BE were updated to upper case
	if (body.subType === 'live') {
		body.subType = 'LIVE';
	}
	// Matches parentPageTypes
	body.restriction = restriction.toUpperCase();

	const result = await cfetch('/wiki/rest/internals/1.0/word-doc/create-parent-page', {
		credentials: 'include',
		method: 'POST',
		body: JSON.stringify(body),
		headers: {
			'Content-Type': 'application/json',
		},
	});

	return result.json();
}

export function getOriginalDraftId(contentId: any): Promise<any> {
	return cfetchWithProcess(
		`/wiki/rest/api/content/${contentId}?status=draft&expand=metadata.properties.originalDraftId`,
	);
}
